import React, { useState, useEffect } from 'react';
import styles from './CoostoSettings.module.css';
import { Button } from '../common/Button/Button';
import { getApiBaseUrl } from '../../services/api';

interface SearchTag {
  name: string;
  query: string;
  active: boolean;
}

interface CoostoConfig {
  projectId: number;
  searchTags: SearchTag[];
  dateRange?: {
    from: string;
    to: string;
  };
  lastSync?: string;
  autoSync: boolean;
  syncInterval: 'hourly' | 'daily' | 'weekly' | 'manual';
}

interface CoostoSettingsProps {
  projectId: string;
  onSyncComplete?: () => void;
}

export const CoostoSettings: React.FC<CoostoSettingsProps> = ({ projectId, onSyncComplete }) => {
  const [config, setConfig] = useState<CoostoConfig | null>(null);
  const [searchTags, setSearchTags] = useState<SearchTag[]>([]);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingTag, setEditingTag] = useState<number | null>(null);
  const [newTag, setNewTag] = useState<SearchTag>({ name: '', query: '', active: true });
  const [showAddForm, setShowAddForm] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDebugLogs, setShowDebugLogs] = useState(false);
  const [debugLogs, setDebugLogs] = useState<any[]>([]);

  const API_BASE = getApiBaseUrl();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCoostoConfig();
  }, [projectId]);

  const fetchCoostoConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setConfig(data.coosto);
      setSearchTags(data.coosto?.searchTags || []);
      if (data.coosto?.dateRange) {
        setDateRange({
          from: new Date(data.coosto.dateRange.from).toISOString().split('T')[0],
          to: new Date(data.coosto.dateRange.to).toISOString().split('T')[0]
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch Coosto config:', error);
      setError('Failed to load Coosto settings');
      setLoading(false);
    }
  };

  const handleSaveTag = async () => {
    const updatedTags = editingTag !== null
      ? searchTags.map((tag, i) => i === editingTag ? newTag : tag)
      : [...searchTags, newTag];

    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/tags`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ searchTags: updatedTags })
      });

      const data = await response.json();

      if (response.ok) {
        setSearchTags(updatedTags);
        setNewTag({ name: '', query: '', active: true });
        setShowAddForm(false);
        setEditingTag(null);
        setError(null);  // Clear any previous errors
      } else {
        setError(data.message || 'Failed to save search tag');
      }
    } catch (error) {
      console.error('Failed to save tag:', error);
      setError('Failed to save search tag: ' + (error as Error).message);
    }
  };

  const handleDeleteTag = async (index: number) => {
    const updatedTags = searchTags.filter((_, i) => i !== index);

    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/tags`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ searchTags: updatedTags })
      });

      const data = await response.json();

      if (response.ok) {
        setSearchTags(updatedTags);
        setError(null);
      } else {
        setError(data.message || 'Failed to delete search tag');
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
      setError('Failed to delete search tag: ' + (error as Error).message);
    }
  };

  const handleToggleTag = async (index: number) => {
    const updatedTags = [...searchTags];
    updatedTags[index].active = !updatedTags[index].active;

    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/tags`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ searchTags: updatedTags })
      });

      const data = await response.json();

      if (response.ok) {
        setSearchTags(updatedTags);
        setError(null);
      } else {
        setError(data.message || 'Failed to update search tag');
      }
    } catch (error) {
      console.error('Failed to toggle tag:', error);
      setError('Failed to update search tag: ' + (error as Error).message);
    }
  };

  const handleDateRangeUpdate = async () => {
    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dateRange })
      });

      const data = await response.json();

      if (response.ok) {
        setError(null);
        // Show success feedback
        const tempSuccess = 'Date range updated successfully';
        setError(null); // Clear any existing errors
        // You could add a success state here if needed
      } else {
        setError(data.message || 'Failed to update date range');
      }
    } catch (error) {
      console.error('Failed to update date range:', error);
      setError('Failed to update date range: ' + (error as Error).message);
    }
  };

  const fetchDebugLogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/debug-logs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setDebugLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch debug logs:', error);
    }
  };

  const clearDebugLogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/debug-logs`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        setDebugLogs([]);
        setError(null);
      }
    } catch (error) {
      console.error('Failed to clear debug logs:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/coosto/projects/${projectId}/coosto/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (response.ok) {
        setSyncResult(data.data);
        if (onSyncComplete) {
          onSyncComplete();
        }
      } else {
        setError(data.message || 'Failed to sync data');
      }
    } catch (error) {
      console.error('Failed to sync:', error);
      setError('Failed to sync sentiment data');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading Coosto settings...</div>;
  }

  return (
    <div className={styles.coostoSettings}>
      <div className={styles.header}>
        <h2>Coosto Integration Settings</h2>
        <Button
          variant="primary"
          onClick={handleSync}
          disabled={syncing || searchTags.length === 0 || !searchTags.some(tag => tag.active)}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.closeError} onClick={() => setError(null)}>×</button>
        </div>
      )}

      {syncResult && (
        <div className={styles.success}>
          Successfully synced {syncResult.messagesCount} messages.
          Sentiment score: {syncResult.sentimentScore}
          <button className={styles.closeSuccess} onClick={() => setSyncResult(null)}>×</button>
        </div>
      )}

      {config?.lastSync && (
        <div className={styles.lastSync}>
          Last synced: {new Date(config.lastSync).toLocaleString('nl-NL')}
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Date Range</h3>
        </div>
        <div className={styles.dateRangeContainer}>
          <div className={styles.formGroup}>
            <label htmlFor="dateFrom">From:</label>
            <input
              id="dateFrom"
              type="date"
              className={styles.input}
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="dateTo">To:</label>
            <input
              id="dateTo"
              type="date"
              className={styles.input}
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            />
          </div>
          <Button
            variant="secondary"
            size="small"
            onClick={handleDateRangeUpdate}
          >
            Update Range
          </Button>
        </div>
        <span className={styles.help}>
          Select the date range for collecting sentiment data from Coosto
        </span>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Search Tags</h3>
          <Button
            variant="secondary"
            size="small"
            onClick={() => {
              setShowAddForm(true);
              setEditingTag(null);
              setNewTag({ name: '', query: '', active: true });
            }}
          >
            Add Tag
          </Button>
        </div>

        {searchTags.length === 0 ? (
          <div className={styles.noTags}>
            No search tags configured. Add search tags to start collecting sentiment data.
          </div>
        ) : (
          <div className={styles.tagsList}>
            {searchTags.map((tag, index) => (
              <div key={index} className={`${styles.tagItem} ${!tag.active ? styles.inactive : ''}`}>
                <div className={styles.tagContent}>
                  <div className={styles.tagHeader}>
                    <span className={styles.tagName}>{tag.name}</span>
                    <span className={`${styles.tagStatus} ${tag.active ? styles.active : ''}`}>
                      {tag.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <code className={styles.tagQuery}>{tag.query}</code>
                </div>
                <div className={styles.tagActions}>
                  <button
                    className={styles.toggleButton}
                    onClick={() => handleToggleTag(index)}
                    title={tag.active ? 'Deactivate' : 'Activate'}
                  >
                    {tag.active ? '✓' : '○'}
                  </button>
                  <button
                    className={styles.editButton}
                    onClick={() => {
                      setEditingTag(index);
                      setNewTag(tag);
                      setShowAddForm(true);
                    }}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeleteTag(index)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddForm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>{editingTag !== null ? 'Edit Search Tag' : 'Add Search Tag'}</h3>

            <div className={styles.formGroup}>
              <label>Tag Name</label>
              <input
                type="text"
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                placeholder="e.g., A10 Roadworks"
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Zoekquery</label>
              <textarea
                value={newTag.query}
                onChange={(e) => setNewTag({ ...newTag, query: e.target.value })}
                placeholder='bijv. ("A10" OR "A10 Noord") AND (wegwerkzaamheden OR asfalt OR omleiding)'
                className={styles.textarea}
                rows={4}
              />
              <div className={styles.queryHelp}>
                <h4>Zo werken zoekoperatoren:</h4>
                <ul>
                  <li><code>AND</code> — Beide termen moeten voorkomen. <em>Voorbeeld: "bouwproject" AND "overlast" vindt alleen berichten die beide woorden bevatten.</em></li>
                  <li><code>OR</code> — Eén van de termen moet voorkomen. <em>Voorbeeld: "geluidsoverlast" OR "trillingen" vindt berichten met minstens één van beide.</em></li>
                  <li><code>( )</code> — Groepeer termen. <em>Voorbeeld: ("A10" OR "A10 Noord") AND (overlast OR werkzaamheden) combineert groepen.</em></li>
                  <li><code>" "</code> — Exacte woordgroep. <em>Voorbeeld: "nieuwe rotonde" zoekt op precies die combinatie.</em></li>
                </ul>
                <p className={styles.queryTip}>Tip: gebruik OR tussen synoniemen en AND tussen verschillende concepten. Hoe specifieker je query, hoe minder irrelevante resultaten (zoals berichten over RWS of andere provincies).</p>
              </div>
            </div>

            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingTag(null);
                  setNewTag({ name: '', query: '', active: true });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveTag}
                disabled={!newTag.name || !newTag.query}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Debug Logs</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                setShowDebugLogs(!showDebugLogs);
                if (!showDebugLogs) {
                  fetchDebugLogs();
                }
              }}
            >
              {showDebugLogs ? 'Hide Logs' : 'View Logs'}
            </Button>
            {showDebugLogs && (
              <Button
                variant="secondary"
                size="small"
                onClick={clearDebugLogs}
              >
                Clear Logs
              </Button>
            )}
          </div>
        </div>

        {showDebugLogs && (
          <div className={styles.debugLogs}>
            {debugLogs.length === 0 ? (
              <p>No debug logs available. Try syncing to generate logs.</p>
            ) : (
              <div className={styles.logsList}>
                {debugLogs.map((log, index) => (
                  <details key={index} className={styles.logEntry}>
                    <summary>
                      {log.timestamp} - {log.type}
                      {log.type === 'response' && log.data?.status && ` (Status: ${log.data.status})`}
                    </summary>
                    <pre className={styles.logContent}>
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};